// frontend/src/CategorizeAndClean/apiClient.js — thin wrapper around authenticatedFetch for /api/categorize-and-clean.
// Errors carry the server's `detail` as the message, plus `.status` and (for 409s) `.productIds`.
import { authenticatedFetch } from '../AuthComponent';

const BASE = '/api/categorize-and-clean';

async function jsonOrThrow(resp) {
  if (!resp.ok) {
    let detail = '';
    let ids;
    try {
      const j = await resp.json();
      detail = typeof j.detail === 'string' ? j.detail : Array.isArray(j.detail) ? j.detail.map((d) => d.msg).join('; ') : '';
      ids = j.product_ids;
    } catch { /* the status is enough */ }
    const err = new Error(detail || `${resp.status} ${resp.statusText}`);
    err.status = resp.status;
    err.productIds = ids;
    throw err;
  }
  return resp.json();
}

const send = (method, path, body) => authenticatedFetch(`${BASE}${path}`, {
  method,
  headers: { 'Content-Type': 'application/json' },
  body: body === undefined ? undefined : JSON.stringify(body),
}).then(jsonOrThrow);

const enc = encodeURIComponent;

// Empty values are dropped; arrays become repeated keys (status=a&status=b).
export function queryString(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v)) v.forEach((x) => qs.append(k, x)); else qs.append(k, v);
  });
  return qs.toString();
}

export const cacApi = {
  status: () => authenticatedFetch(`${BASE}/status`).then(jsonOrThrow),
  products: (params) => authenticatedFetch(`${BASE}/products?${queryString(params)}`).then(jsonOrThrow),
  product: (id) => authenticatedFetch(`${BASE}/products/${id}`).then(jsonOrThrow),
  editFacet: (id, facet, value) => send('POST', `/products/${id}/facets/${enc(facet)}`, { value }),
  categoryOthers: (id, category) => authenticatedFetch(`${BASE}/products/${id}/category-others?category=${enc(category)}`).then(jsonOrThrow),
  applyCategoryToAll: (id, old, value, oldName) => send('POST', `/products/${id}/category/apply-to-all`, { old, new: value, old_name: oldName }),
  approve: (product_ids, as_is = false) => send('POST', '/products/approve', { product_ids, as_is }),
  unapprove: (product_ids) => send('POST', '/products/unapprove', { product_ids }),
  write: (product_ids, dryRun = false) => send('POST', `/products/write?dry_run=${dryRun ? 'true' : 'false'}`, { product_ids }),
  searchTaxonomy: (q) => authenticatedFetch(`${BASE}/taxonomy/search?q=${enc(q)}`).then(jsonOrThrow),
  dictionary: () => authenticatedFetch(`${BASE}/dictionary`).then(jsonOrThrow),
  pending: () => authenticatedFetch(`${BASE}/dictionary/pending`).then(jsonOrThrow),
  valueCounts: (facet) => authenticatedFetch(`${BASE}/dictionary/${enc(facet)}/counts`).then(jsonOrThrow),
  renameValue: (facet, value, next) => send('POST', `/dictionary/${enc(facet)}/values/${enc(value)}/rename`, { new: next }),
  removeValue: (facet, value) => send('DELETE', `/dictionary/${enc(facet)}/values/${enc(value)}`),
  addValue: (facet, value, attrs) => send('POST', `/dictionary/${enc(facet)}/values`, { value, attrs }),
  setValueStatus: (facet, value, status) => send('POST', `/dictionary/${enc(facet)}/values/${enc(value)}/status`, { status }),
  addAlias: (facet, alias, value) => send('POST', `/dictionary/${enc(facet)}/aliases`, { alias, value }),
  deleteAlias: (facet, alias) => send('DELETE', `/dictionary/${enc(facet)}/aliases/${enc(alias)}`),
  runs: () => authenticatedFetch(`${BASE}/runs`).then(jsonOrThrow),
  run: (id) => authenticatedFetch(`${BASE}/runs/${id}`).then(jsonOrThrow),
  startRun: (kind, options, ceiling_dollars) => send('POST', '/runs', { kind, options, ceiling_dollars }),
  analyzeProduct: (id, force = true) => send('POST', `/products/${id}/analyze`, { force }),
  analyzeProducts: (product_ids, force = false, batch = false) => send('POST', '/products/analyze', { product_ids, force, batch }),
  setCategory: (product_ids, value) => send('POST', '/products/category', { product_ids, value }),
  cancelRun: (id) => send('POST', `/runs/${id}/cancel`),
};
